import { useCallback, useEffect, useLayoutEffect, memo } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation, useParams } from 'react-router';

import { GlobalBanner } from '@/components/atoms/GlobalBanner/GlobalBanner';
import Loading from '@/components/atoms/Loading/Loading';
import ChatNavigationIframe from '@/components/organisms/IframeNavigation/ChatNavigationIframe';
import MainNavigationIframe from '@/components/organisms/IframeNavigation/MainNavigationIframe';
import { MainNavigation } from '@/components/organisms/MainNavigation/MainNavigation';
import AccountSettingsRouter from '@/components/pages/AccountSettings/AccountSettingsRouter';
import ChatsRouter from '@/components/pages/Chats/ChatsRouter';
import DesksRouter from '@/components/pages/Desks/DeskRouter';
import Home from '@/components/pages/Home/Home';
import { IntegrationsNoEnvironment } from '@/components/pages/Integrations/IntegrationsNoEnvironment';
import ManageRouter from '@/components/pages/ManageAccess/ManageRouter';
import NotFound from '@/components/pages/NotFound/NotFound';
import { useRtmDesk } from '@/hooks/rtm/useRtmDesk';
import { useAccount } from '@/hooks/useAccount';
import useDesks from '@/hooks/useDesks';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import useMembers from '@/hooks/useMembers';
import { usePermissions } from '@/hooks/usePermissions';
import useUser from '@/hooks/useUser';
import AIAgentsRouter from '@/modules/aiAgents/AIAgentsRouter';
import AnalyticsRouter from '@/modules/analytics/AnalyticsRouter';
import { BroadcastsRouter } from '@/modules/broadcast/components/BroadcastsRouter';
import { CampaignsRouter } from '@/modules/campaigns/CampaignsRouter';
import { CollectionsRouter } from '@/modules/collections/CollectionsRouter';
import { CommandPalette } from '@/modules/commandPalette/CommandPalette';
import { CommandPaletteProvider } from '@/modules/commandPalette/CommandPaletteProvider';
import { ContactsRouter, DebtsRouter } from '@/modules/contacts/ContactsRouter';
import { DevelopersSettingsRouter } from '@/modules/developerTools/DevelopersSettingsRouter';
import { DeveloperToolsRouter } from '@/modules/developerTools/DeveloperToolsRouter';
import { useRtmOutboundDelivered } from '@/modules/humanChat/hooks/useRtmOutboundDelivered';
import { useRtmOutboundFailed } from '@/modules/humanChat/hooks/useRtmOutboundFailed';
import { useRtmOutboundProcessed } from '@/modules/humanChat/hooks/useRtmOutboundProcessed';
import { useRtmOutboundSent } from '@/modules/humanChat/hooks/useRtmOutboundSent';
import { RulesNoEnvironment } from '@/modules/rules/pages/Rules/RulesNoEnvironment';
import { closeBanner } from '@/redux/banners/actions';
import { selectBanners } from '@/redux/banners/selectors';
import { setAccountRole, setPermissions } from '@/redux/permissions/actions';
import { setAccountId, setAccountSlug, setDeskId } from '@/redux/session/actions';
import { selectAccountId, selectDeskId } from '@/redux/session/selectors';
import { selectIframeView } from '@/redux/user/selectors';
import { trackAccount, trackRoles } from '@/segment/segment';

const Account: React.FC = () => {
  const { slug } = useParams();
  const { permissions } = usePermissions();
  const dispatch = useDispatch();
  const { getUserStatus } = useUser();

  // This can be reused in the future if needed.
  // useFeatureUpgrade();
  const { account, accountRole, accountStatus, setLastUsedAccountSlug, isValidSlug } = useAccount();
  const { desks, listStatus } = useDesks();
  // `role_ids` are on the members payload, not on the account's member list.
  const { member } = useMembers();
  const { t } = useTranslation();

  const iframeView = useSelector(selectIframeView);
  const banners = useSelector(selectBanners);
  const { pathname } = useLocation();
  const isChatPage = pathname.includes('/chats');
  const selectedAccountId = useSelector(selectAccountId);

  useEffect(() => {
    if (slug && !selectedAccountId) {
      dispatch(setAccountId(account?.account_id));
    }
  }, [dispatch, slug, selectedAccountId, account?.account_id]);

  // eslint-disable-next-line no-restricted-syntax -- Account manages deskId validation/fallback
  const deskId = useSelector(selectDeskId);
  useRtmDesk();
  // Handles delayed WhatsApp delivery errors from humanChat's NewMessageModal.
  // After submitting a new message, WhatsApp may return an async failure. Mounted
  // here at the global level so the toast still triggers if the user navigates
  // away from conversations before the error arrives.
  useRtmOutboundFailed();
  // Similarly, the outbound:processed success toast with conversation link is
  // mounted here so it fires regardless of which page the agent is viewing.
  useRtmOutboundProcessed();
  useRtmOutboundSent();
  useRtmOutboundDelivered();

  const onBannerDismiss = useCallback(() => {
    dispatch(closeBanner());
  }, [dispatch]);

  useLayoutEffect(() => {
    dispatch(setAccountSlug(slug));
  }, [dispatch, slug]);

  // Load feature flags for the current account
  // DON'T REMOVE: ensures feature flags are loaded
  useFeatureFlag();

  useEffect(() => {
    // Wait for desks to load to avoid race condition with middleware restoring deskId
    if (listStatus !== 'success') {
      return;
    }

    // No desks exist, clear any stale deskId
    if (isEmpty(desks)) {
      if (deskId) {
        dispatch(setDeskId(null));
      }
      return;
    }

    // Fall back to first desk if current deskId is invalid (null, deleted, or lost access)
    const isDeskValid = deskId && desks.some((d) => d.desk_id === deskId);
    if (!isDeskValid) {
      dispatch(setDeskId(desks[0]?.desk_id));
    }
  }, [deskId, desks, listStatus, dispatch]);

  useEffect(() => {
    if (account?.slug) {
      setLastUsedAccountSlug(account.slug);
    }
  }, [account?.slug, setLastUsedAccountSlug]);

  useEffect(() => {
    if (account?.account_id) {
      dispatch(setAccountId(account?.account_id));
      dispatch(setAccountRole(accountRole));
      if (permissions) {
        dispatch(setPermissions(permissions));
      }
    }
  }, [dispatch, account?.account_id, accountRole, permissions]);

  useEffect(() => {
    if (account) {
      trackAccount(account);
    }
  }, [account]);

  // Waits for the membership: before it lands, a member with roles and a member
  // with none are the same `undefined`, and reporting `none` for both would put
  // the wrong role on the profile until the query resolves.
  useEffect(() => {
    if (account && member) {
      trackRoles(account.account_id, member.role_ids, accountRole);
    }
  }, [account, member, accountRole]);

  // Show error page if account slug is not valid
  if (getUserStatus === 'error' && !isValidSlug()) {
    return <NotFound entity={t('common.account')} value={slug} />;
  }

  if (accountStatus === 'pending') {
    return <Loading />;
  }

  if (accountStatus === 'success' && !account) {
    return <NotFound />;
  }

  if (accountStatus === 'error') {
    return <NotFound entity={t('common.account')} value={slug} />;
  }

  return (
    <CommandPaletteProvider>
      <div className="flex h-screen flex-col">
        {banners.props && <GlobalBanner {...banners.props} onDismiss={onBannerDismiss} />}
        {!iframeView && <MainNavigation />}
        {iframeView && !isChatPage && <MainNavigationIframe />}
        {iframeView && isChatPage && <ChatNavigationIframe />}

        <div className="min-h-0 flex-1 overflow-hidden">
          <Routes>
            <Route path="/" element={<Home />} />

            <Route path="/environments/*" element={<DesksRouter />} />
            <Route path="/devtools/*" element={<DeveloperToolsRouter />} />
            <Route path="/knowledge-bases/*" element={<CollectionsRouter />} />

            <Route path="/ai-agents/*" element={<AIAgentsRouter />} />
            <Route path="/broadcasts/*" element={<BroadcastsRouter />} />
            <Route path="/campaigns/*" element={<CampaignsRouter />} />
            <Route path="/contacts/*" element={<ContactsRouter />} />
            <Route path="/debts" element={<DebtsRouter />} />

            <Route path="/chats/*" element={<ChatsRouter />} />
            <Route path="/integrations" element={<IntegrationsNoEnvironment />} />
            <Route path="/rules" element={<RulesNoEnvironment />} />
            <Route path="/account/manage/*" element={<ManageRouter />} />
            <Route path="/account/developers/*" element={<DevelopersSettingsRouter />} />
            <Route path="/account/*" element={<AccountSettingsRouter />} />
            <Route path="/analytics/*" element={<AnalyticsRouter />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
        {!iframeView && <CommandPalette />}
      </div>
    </CommandPaletteProvider>
  );
};

export default memo(Account);
